import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { AppProvider } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Partners from "@/pages/Partners";
import Items from "@/pages/Items";
import Documents from "@/pages/Documents";
import DocumentEditor from "@/pages/DocumentEditor";
import DocumentView from "@/pages/DocumentView";
import Payments from "@/pages/Payments";
import Stock from "@/pages/Stock";
import Accounting from "@/pages/Accounting";
import Members from "@/pages/Members";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";
import Backoffice from "@/pages/Backoffice";
import Declarations from "@/pages/Declarations";
import FirmSpace from "@/pages/FirmSpace";
import Justificatifs from "@/pages/Justificatifs";
import Bank from "@/pages/Bank";
import Assets from "@/pages/Assets";
import Closing from "@/pages/Closing";
import Payroll from "@/pages/Payroll";
import { readDemo } from "@/context/AppContext";
import PayPage from "@/pages/PayPage";
import EmployeePortal from "@/pages/EmployeePortal";
import ReceiptsQueue from "@/pages/ReceiptsQueue";
import DemoInvite from "@/pages/DemoInvite";
import { LangToggle } from "@/components/layout/AppShell";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";

const AuthLayout = ({ children }) => {
  const { t } = useApp();
  return (
  <div className="relative min-h-screen grid lg:grid-cols-2">
    <div className="absolute top-4 end-4 z-10"><LangToggle /></div>
    <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white grain">
      <Logo dark size={44} />
      <p className="max-w-md text-3xl font-semibold leading-snug">{t("auth_pitch")}</p>
      <p className="text-sm text-slate-400">TTN · El Fatoora · TEIF 1.8.7</p>
    </div>
    <div className="flex items-center justify-center p-6 pt-16">{children}</div>
  </div>
  );
};

const Protected = ({ children }) => (readDemo() ? children : (
  <>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
));

const Home = () => (
  <>
    <Show when="signed-in"><Navigate to="/app" replace /></Show>
    <Show when="signed-out"><Landing /></Show>
  </>
);

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <AppProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pay/:token" element={<PayPage />} />
            <Route path="/salarie/:token" element={<EmployeePortal />} />
            <Route path="/demo/:slug" element={<DemoInvite />} />
            <Route path="/sign-in/*" element={<AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>} />
            <Route path="/sign-up/*" element={<AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<Partners kind="client" />} />
              <Route path="suppliers" element={<Partners kind="supplier" />} />
              <Route path="items" element={<Items />} />
              <Route path="sales" element={<Documents side="sale" />} />
              <Route path="purchases" element={<Documents side="purchase" />} />
              <Route path="documents/new" element={<DocumentEditor />} />
              <Route path="documents/:id" element={<DocumentView />} />
              <Route path="documents/:id/edit" element={<DocumentEditor />} />
              <Route path="payments" element={<Payments />} />
              <Route path="stock" element={<Stock />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="members" element={<Members />} />
              <Route path="audit" element={<Audit />} />
              <Route path="settings" element={<Settings />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="backoffice" element={<Backoffice />} />
              <Route path="declarations" element={<Declarations />} />
              <Route path="firm" element={<FirmSpace />} />
              <Route path="justificatifs" element={<Justificatifs />} />
              <Route path="bank" element={<Bank />} />
              <Route path="assets" element={<Assets />} />
              <Route path="closing" element={<Closing />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="receipts" element={<ReceiptsQueue />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-center" />
        </AppProvider>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
